'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ParsedRoster, RosterFormat } from '@/lib/types';
import type { ParseResult } from '@/lib/parse.worker';

interface Props {
  onParsed: (roster: ParsedRoster) => void;
}

export default function RosterUploader({ onParsed }: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [format, setFormat] = useState<RosterFormat | null>(null);
  const [parsing, setParsing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const procRef = useRef<((file: File) => void) | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const worker = new Worker(new URL('../lib/parse.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (e: MessageEvent<ParseResult & { _req?: number }>) => {
      if (e.data._req !== undefined && e.data._req !== requestIdRef.current) return;
      setParsing(false);
      if (e.data.error) {
        setError(e.data.error);
      } else if (e.data.roster) {
        setFormat(e.data.roster.format);
        onParsed(e.data.roster);
      }
    };
    worker.onerror = () => {
      setParsing(false);
      setError('Error parsing file');
    };
    return () => worker.terminate();
  }, [onParsed]);

  const processFile = useCallback(async (file: File) => {
    setError(null);
    setParsing(true);
    setFileName(file.name);
    setFormat(null);

    if (file.size > 50 * 1024 * 1024) {
      setError('File is too large. Maximum size is 50 MB.');
      setParsing(false);
      return;
    }

    if (!file.name.match(/\.xls[xm]?$/i)) {
      setError('Please upload an Excel file (.xls, .xlsx or .xlsm)');
      setParsing(false);
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const reqId = ++requestIdRef.current;
      workerRef.current?.postMessage({ buffer: arrayBuffer, _req: reqId });
    } catch (err) {
      setParsing(false);
      setError(`Error reading file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, []);

  useEffect(() => {
    procRef.current = processFile;
  }, [processFile]);

  // Native event listener - this works (React onChange does NOT)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handler = () => {
      const file = el.files?.[0];
      if (file && procRef.current) procRef.current(file);
    };
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items?.length) setIsDragging(true);
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-md">
      <label
        className={`block w-full border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragging
            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
            : 'border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 hover:border-zinc-400 dark:hover:border-zinc-500'
        }`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="mb-3 text-4xl">{isDragging ? '📂' : '📁'}</div>
        <p className="text-zinc-600 dark:text-zinc-400 text-base font-medium">
          {fileName
            ? `File selected: ${fileName}`
            : isDragging
            ? 'Release to drop your file here!'
            : 'Drop roster file here'}
        </p>
        <p className="text-zinc-400 text-sm mt-2">
          <span className="text-emerald-600 dark:text-emerald-400 font-bold underline">
            Browse
          </span>
        </p>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
        />
      </label>

      {error && <p className="text-sm text-red-500 font-medium">⚠️ {error}</p>}
      {parsing && <p className="text-sm text-zinc-500">Parsing roster…</p>}
      {format && !parsing && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400">
          Detected: {format === 'mmc' ? 'MMC (Monash Medical Centre)' : format === 'vhh' ? 'VHH (Valley Hospital)' : 'Casey Hospital'}
        </p>
      )}
    </div>
  );
}
